import { useState, type ReactNode } from 'react'
import { Button, Card } from '../../ui'
import { requestScan, type ScanFreshness } from '../../lib/api'
import './live.css'
import { SupportLink } from '../../lib/support'
import { listOf, notLicensedAreaCount, notLicensedAreas, partlyNotLicensedAreas } from './notLicensed'
import {
  checkName, gapDescriptions, PARTLY_READ_NOT_BETTER, PARTLY_READ_WHY, partlyReadAreas, partlyReadPhrase, unconfirmedAreas,
} from './partlyRead'
import type { PartlyReadArea } from './partlyRead'

/**
 * Asks for a new assessment.
 *
 * Rendered beside the freshness banner rather than in a settings page, because the moment a person
 * decides they want fresher numbers is the moment they are reading how old these ones are.
 *
 * Until this existed there was NO way to ask for a scan. The only thing that ever queued one was
 * tenant verification, so a customer received a single assessment on the day they connected and
 * nothing afterwards -- while this very component told them "running a new assessment is worth
 * trying" and the banner beside it said "a new assessment is queued", for a queue nothing could
 * add to. There was no workaround: this product has no disconnect route, so the only way a second
 * scan ever happened was a new workspace connecting a tenant for the first time.
 */
/**
 * @param label
 * What the button says. The dashboard's header calls it "Assess now" because that is the wording
 * of the approved design and the button sits in the page header rather than inside this card;
 * everywhere else it stays the longer sentence, which is clearer when it appears mid-page.
 */
export function RescanButton({
  onQueued,
  label = 'Run a new assessment',
}: {
  onQueued: () => void
  label?: string
}) {
  const [state, setState] = useState<
    { kind: 'idle' | 'asking' | 'queued' } | { kind: 'failed'; status: number }
  >({ kind: 'idle' })

  async function ask() {
    setState({ kind: 'asking' })
    const result = await requestScan()

    if (!result.ok) {
      // WHY, kept. Every failure used to read "try again in a moment" -- so a read-only auditor
      // retried a button that would refuse them every time, and a workspace with nothing connected
      // was told to wait for a tenant that did not exist.
      setState({ kind: 'failed', status: result.status })
      return
    }

    // 'queued' and 'already_queued' are both success. A person who pressed twice does not need to
    // be told off for it.
    setState({ kind: 'queued' })
    onQueued()
  }

  if (state.kind === 'queued') {
    return (
      <p className="freshness-body" role="status">
        {/* No fixed time: one scanner serves every workspace, one assessment at a time, so a
            request waits behind whatever is already running. */}
        Queued. It starts as soon as the scanner is free.
      </p>
    )
  }

  return (
    <>
      {/*
        Primary, like "Pick a plan". Asking for a fresh assessment is the main thing a person
        comes to this card to do, and it was rendered as a secondary outline while the upsell
        beside it carried the only filled button on the page.
      */}
      <Button variant="primary" onClick={() => void ask()} disabled={state.kind === 'asking'}>
        {state.kind === 'asking' ? 'Asking…' : label}
      </Button>
      {state.kind === 'failed' ? <p className="freshness-body" role="alert">{rescanFailure(state.status)}</p> : null}
    </>
  )
}

/**
 * What a refused scan request means, in the words the server uses. Hardcoded because ApiResult
 * carries only a status; the 403 sentence is the one ScanEndpoints sends with `read_only`.
 */
function rescanFailure(status: number): ReactNode {
  if (status === 403) {
    return 'Your access to this workspace is read-only. An owner or an admin can run an assessment.'
  }
  if (status === 409) {
    return (
      <>
        No Microsoft 365 tenant is connected, so there is nothing to assess.{' '}
        <a href="/connect">Connect your Microsoft 365 tenant</a>.
      </>
    )
  }
  if (status === 401) {
    return (
      <>
        Your sign-in has ended. <a href="/auth/sign-in">Sign in again</a> to run an assessment.
      </>
    )
  }
  return 'That did not go through. If your tenant is still connected, try again in a moment.'
}

/**
 * How current the numbers beside it are.
 *
 * This is why the read API carries freshness on every response rather than behind a separate
 * call, and why it renders above the findings rather than next to them. A tenant where half the
 * checks could not run has FEWER findings than a healthy tenant. Without this banner those two
 * produce the same screen, and the one that is wrong reads as good news — the single most
 * damaging thing this product could say to somebody who trusts it.
 *
 * So the quiet case is the only one that renders quietly, and it is reached only by falling
 * through every other branch AND by the server's own `complete` flag. Ordering matters here and
 * has been got wrong once already: a real scan status is checked before the queue, or a tenant
 * with a rescan pending reports "first assessment queued" over results it already has.
 */
/**
 * What each area status is called on screen.
 *
 * "Waiting" rather than "pending", because a person watching this is waiting; the database's word
 * for it is not theirs. An unrecognised status falls through to itself rather than to a friendly
 * guess -- a state nobody has named yet should look unfamiliar, not fine.
 */
const AREA_STATE: Record<string, string> = {
  pending: 'Waiting',
  running: 'Assessing now',
  complete: 'Done',
  partial: 'Partly done',
  failed: 'Could not finish',
}

/**
 * Renders a server-supplied reason as the end of a sentence.
 *
 * Returns ": that reason." or just "." when there is none, and adds no second full stop to a
 * reason that already ends in one.
 */
function sentence(reason: string | null): string {
  if (!reason) return '.'

  const trimmed = reason.trim()
  return /[.!?]$/.test(trimmed) ? `: ${trimmed}` : `: ${trimmed}.`
}

/**
 * Whether asking for a new assessment makes sense right now.
 *
 * Not while a scan is running, and not while one is already queued -- the server would refuse
 * politely, but offering a button whose only outcome is "that was already true" teaches people
 * the button does nothing.
 *
 * Exported because the dashboard hosts the button in its page header, and a second copy of this
 * rule there would be a second place for it to drift.
 */
export function canRescan(freshness: ScanFreshness): boolean {
  return freshness.status !== 'running' && !freshness.scanPending
}

/**
 * @param showRescan
 * False where the page puts the rescan button somewhere of its own. The dashboard's header
 * carries it, per the approved design, and two buttons queuing the same scan on one screen is
 * how a person ends up believing the first one did not work.
 */
export function Freshness({
  freshness,
  onScanQueued = () => {},
  showRescan = true,
}: {
  freshness: ScanFreshness
  onScanQueued?: () => void
  showRescan?: boolean
}) {
  const rescan = showRescan && canRescan(freshness) ? (
    <RescanButton onQueued={onScanQueued} />
  ) : null

  const rescanNote = freshness.scanPending ? (
    <p className="freshness-body">A new assessment is queued and has not replaced these results yet.</p>
  ) : null

  // ── a scan that ran, in whatever state it ended ─────────────────────────────────────

  if (freshness.status === 'running') {
    return (
      <Card className="freshness freshness-pending">
        <div className="freshness-head">
          <Spinner /> Assessment running
          {freshness.areasTotal > 0 ? (
            <span className="freshness-count">
              {freshness.areasCompleted} of {freshness.areasTotal} areas
            </span>
          ) : null}
        </div>
        <p className="freshness-body">
          Results appear as each area finishes. What is below is incomplete until it does.
        </p>

        {/*
          WHAT IS BEING ASSESSED. The count alone cannot separate a scan working through a
          directory from one that has stopped, and both look like "0 of 2" for minutes at a time.
          These states have always been recorded per area; nothing displayed them.
        */}
        {freshness.areas.length > 0 ? (
          <ul className="freshness-areas">
            {freshness.areas.map((a) => (
              // An area finished only because nothing in it is licensed (0158) is not "Done": that
              // word, in the green of a finished area, is the picture of an area assessed and clean.
              <li
                key={a.area}
                className={`freshness-area is-${a.notLicensed === true ? 'not-licensed' : a.status}`}
              >
                <span className="freshness-area-name">{a.area}</span>
                <span className="freshness-area-state">
                  {a.notLicensed === true ? 'Not licensed' : (AREA_STATE[a.status] ?? a.status)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    )
  }

  if (freshness.status === 'partial') {
    // The dangerous case, and why areasCompleted is a number rather than a flag. A customer told
    // "some checks failed" cannot act; one told "one of four areas finished" knows exactly how
    // much of this page to distrust.
    return (
      <Card className="freshness freshness-partial">
        <div className="freshness-head">
          Partial results — {freshness.areasCompleted} of {freshness.areasTotal} areas completed
        </div>
        <p className="freshness-body">
          {/*
            The full stop matters. Without it the sanitised reason ran straight into the next
            sentence -- "could not run: unreachable: Data Findings from the areas that did not
            complete are missing" -- which reads as one broken sentence and made "Data" look like
            part of the following clause rather than the name of the area that failed. The reason
            comes from the server and may or may not end in punctuation of its own.
          */}
          Some checks could not run{sentence(freshness.failureReason)}{' '}
          Findings from the areas that did not complete are missing from this page.{' '}
          <strong>Treat this as an incomplete picture, not a clean one.</strong>
        </p>
        <IncompleteAreas freshness={freshness} />
        <NotLicensedAreas freshness={freshness} />
        <FinishedButPartlyRead freshness={freshness} />
        {rescanNote}
        {rescan}
        {freshness.asOf ? <AsOf at={freshness.asOf} /> : null}
      </Card>
    )
  }

  if (freshness.status === 'failed') {
    return (
      <Card className="freshness freshness-failed">
        <div className="freshness-head">Last assessment failed</div>
        <p className="freshness-body">
          {/* Through sentence(), like the partial banner: the reason runs into the next sentence
              otherwise ("... generation 4 Anything below ..."). */}
          The assessment did not complete{sentence(freshness.failureReason)} Anything below is from
          an earlier run and may be out of date.
        </p>
        <IncompleteAreas freshness={freshness} />
        <NotLicensedAreas freshness={freshness} />
        <FinishedButPartlyRead freshness={freshness} />
        {rescanNote}
        {rescan}
        {freshness.asOf ? <AsOf at={freshness.asOf} /> : null}
      </Card>
    )
  }

  if (freshness.inconsistent) {
    // Status says complete, the area counts do not. Its own state rather than folded into
    // "partial", because this is a defect in our scan pipeline and not a fact about the customer's
    // tenant — and hiding it behind a message they see every day is how it would go unnoticed.
    return (
      <Card className="freshness freshness-failed">
        <div className="freshness-head">Assessment results are incomplete</div>
        <p className="freshness-body">
          This scan is recorded as finished but covered {freshness.areasCompleted} of{' '}
          {freshness.areasTotal} areas. That is a fault on our side, not a result about your
          tenant. <strong>Do not read this page as a complete assessment.</strong> We are looking
          at it; running a new assessment is worth trying.
        </p>
        {rescan}
        {freshness.asOf ? <AsOf at={freshness.asOf} /> : null}
      </Card>
    )
  }

  // ── nothing has produced results yet ────────────────────────────────────────────────

  if (freshness.scanPending && freshness.status === null) {
    return (
      <Card className="freshness freshness-pending">
        <div className="freshness-head">
          <Spinner /> First assessment queued
        </div>
        <p className="freshness-body">
          The tenant is connected and a scan is waiting to run.{' '}
          <strong>An empty list below does not mean the tenant is clean</strong> — it means nothing
          has been checked yet.
        </p>
      </Card>
    )
  }

  if (freshness.status === null) {
    // No connection, no queue, no scan. Distinct from pending on purpose: the action a customer
    // needs to take is different, and "queued" would be a promise nothing is going to keep.
    return (
      <Card className="freshness freshness-unknown">
        <div className="freshness-head">No tenant has been scanned</div>
        <p className="freshness-body">
          Nothing on this page describes a Microsoft tenant yet.{' '}
          {/* A plain <a>: this renders outside a Router in its tests, and /connect is where a
              workspace that abandoned Microsoft consent picks it up again. It used to say "from
              Settings", and Settings pointed to an Onboarding page production does not have. */}
          <a href="/connect">Connect your Microsoft 365 tenant</a>, and the first assessment runs
          as soon as the connection is verified.
        </p>
      </Card>
    )
  }

  // ── complete, and only if the server says so ────────────────────────────────────────

  // Both, and the order matters. Checking only `complete` let an unrecognised terminal status --
  // a `cancelled` added to the worker after this screen shipped, arriving with complete: true --
  // fall into the quiet line, which is exactly what the comment below claimed it could not do. A
  // confidently wrong comment about a safety property is worse than no comment.
  if (freshness.status === 'complete' && freshness.complete) {
    /*
     * COMPLETE IS NOT ALWAYS WHOLE, and this line said it was.
     *
     * Every check can run while one of them reads only part of what it asked Microsoft for -- a
     * truncated page, a continuation link it could not parse, records it could not classify. Those
     * findings are reported and nothing they did not see is closed, but the scan finishes and the
     * area is marked complete, because failing it would freeze resolution for every other area.
     *
     * So the qualification has to appear HERE, or the safety work upstream ends at a sentence that
     * quietly overstates it. An assessment that finished is not the same as one that saw
     * everything, and the customer is the person that distinction is for.
     *
     * NAMED, NOT COUNTED (review, 2026-09-27). "2 areas read only part of what Microsoft holds" said
     * neither which areas, which checks nor why, and was not even true of every such check: one read
     * all its devices and could not judge eight of them. The line now names the areas, and the
     * checks and the reason are one click away beneath it (partlyRead.ts).
     */
    const partlyRead = partlyReadAreas(freshness)

    // An area whose record of partly-read checks is there but could not be read (a key gone): not
    // named above, since nothing says which checks, but not left to read as whole either -- the
    // printed report already says it could not confirm them (review, 2026-09-28).
    const unconfirmed = unconfirmedAreas(freshness)

    /*
     * NOR IS COMPLETE ALWAYS ASSESSED (0158, owner 2026-09-26).
     *
     * An area whose every control reads a service this tenant has no licence for -- and which
     * Microsoft refused to read -- finishes without failing, so it is counted among the completed
     * areas. Left there, "Assessment complete — 4 areas" says four areas were read when two were
     * not read at all. So they are counted apart and named, and an area assessed only in part
     * (Endpoint's Defender controls beside its Intune one) is named too: "4 areas" alone would
     * imply Endpoint was read whole.
     */
    const notLicensedCount = notLicensedAreaCount(freshness)
    const notLicensedNames = notLicensedAreas(freshness)
    const partlyNotLicensed = partlyNotLicensedAreas(freshness)
    const named = notLicensedNames.length > 0 ? ` (${notLicensedNames.join(', ')})` : ''
    const notLicensedPart =
      notLicensedCount === 0
        ? ''
        : freshness.areasTotal > 0
          ? `, ${notLicensedCount} not licensed${named}`
          // No total to hang it on (a server that sent none): still said, never dropped.
          : ` — ${notLicensedCount === 1 ? '1 area' : `${notLicensedCount} areas`} not licensed${named}`

    const hasKnownGap = notLicensedCount > 0 || partlyNotLicensed.length > 0 || partlyRead.length > 0
    const coverageLimited = hasKnownGap || unconfirmed.length > 0

    return (
      <div
        className={coverageLimited ? 'freshness-ok freshness-limited' : 'freshness-ok'}
        role={coverageLimited ? 'region' : undefined}
        aria-label={coverageLimited ? 'Assessment coverage' : undefined}
      >
        {coverageLimited ? <h2 className="freshness-coverage-heading">Assessment finished — coverage {hasKnownGap ? 'limited' : 'unconfirmed'}</h2> : null}
        <div className="freshness-status">
          Assessment run finished{freshness.areasTotal > 0 ? ` — ${freshness.areasTotal} ${freshness.areasTotal === 1 ? 'area' : 'areas'} processed` : ''}
          {notLicensedPart}
          {partlyNotLicensed.length > 0 ? ` · part of ${listOf(partlyNotLicensed)} not licensed` : ''}
          {partlyRead.length > 0 ? ` · ${partlyReadPhrase(partlyRead)}` : ''}
          {unconfirmed.length > 0
            ? ` · could not confirm that every check in ${listOf(unconfirmed)} read its evidence in full`
            : ''}
          {freshness.asOf ? (
            <>
              {' · '}
              <AsOf at={freshness.asOf} inline />
            </>
          ) : null}
          {freshness.scanPending ? ' · a new assessment is queued' : ''}
        </div>
        {coverageLimited ? <p className="freshness-coverage-note">A completed run is not an all-clear. Review the coverage gaps before interpreting the finding counts.</p> : null}
        <PartlyReadDetails areas={partlyRead} />
        {/*
          The rescan action stays secondary, after the timestamp and any coverage warning. A run
          with no recorded gaps stays quiet, without implying that the tenant is secure.
        */}
        {rescan ? <div className="freshness-ok-action">{rescan}</div> : null}
      </div>
    )
  }

  // A status this build does not recognise — a worker state added after this screen shipped, or a
  // value nothing here anticipated. It lands loudly rather than falling through to the quiet line,
  // which is where it used to land: TypeScript's union describes what we expect a JSON response to
  // contain, and checks nothing about what actually arrives.
  return (
    <Card className="freshness freshness-partial">
      <div className="freshness-head">Assessment state not recognised</div>
      <p className="freshness-body">
        The last assessment reported a state this page does not know how to interpret
        {freshness.status ? ` ("${freshness.status}")` : ''}.{' '}
        <strong>Do not read this page as a complete assessment.</strong> Please tell us at{' '}
        <SupportLink subject="Unrecognised assessment state" /> — that is a fault on our side.
      </p>
      {freshness.asOf ? <AsOf at={freshness.asOf} /> : null}
    </Card>
  )
}

/**
 * Which areas did not complete, by name. The banners said "some areas" without ever saying which,
 * so a reader could not tell whether the part they cared about was the part that was missing.
 */
function IncompleteAreas({ freshness }: { freshness: ScanFreshness }) {
  const missing = freshness.areas.filter((a) => a.status !== 'complete').map((a) => a.area)
  if (missing.length === 0) return null
  return <p className="freshness-body">Not complete: {missing.join(', ')}.</p>
}

/**
 * Which areas finished only because nothing in them is licensed (0158). They are among the
 * "completed" areas in the partial and failed banners' counts, so without this line a banner reading
 * "3 of 4 areas completed" invites the reader to believe three were assessed.
 */
function NotLicensedAreas({ freshness }: { freshness: ScanFreshness }) {
  const names = notLicensedAreas(freshness)
  if (names.length === 0) return null
  return <p className="freshness-body">Not licensed, so not assessed: {names.join(', ')}.</p>
}

/**
 * On the partial and failed banners: the areas that DID finish, where some checks still saw only
 * part of what they needed. Those areas are among the "completed" in the banner's count, and without
 * this line the reader would take them as whole.
 */
function FinishedButPartlyRead({ freshness }: { freshness: ScanFreshness }) {
  const areas = partlyReadAreas(freshness, { finishedOnly: true })
  if (areas.length === 0) return null

  const phrase = partlyReadPhrase(areas)
  return (
    <>
      <p className="freshness-body">
        Of the areas that finished, {phrase}.
      </p>
      <PartlyReadDetails areas={areas} />
    </>
  )
}

/**
 * Which checks saw only part of what they needed, and why -- one click from the line that counts
 * them.
 *
 * A native disclosure, like the coverage card's "See all": keyboard and screen reader behaviour come
 * with the element, and the quiet complete line stays one line until somebody asks. Closed by
 * default for the same reason. The areas are named on the line itself, so nothing that says how
 * much to trust the page is hidden behind the click; what is behind it is the detail.
 */
function PartlyReadDetails({ areas }: { areas: PartlyReadArea[] }) {
  if (areas.length === 0) return null

  return (
    <details className="freshness-partly">
      <summary className="freshness-partly-summary">Which checks, and why</summary>
      <div className="freshness-partly-body">
        <p className="freshness-partly-why">
          {PARTLY_READ_WHY} <strong>{PARTLY_READ_NOT_BETTER}</strong>
        </p>
        {areas.some((area) => area.checks.some((check) => check.gaps?.length))
          ? <p className="settings-note">Counts are per reason and may overlap; they are not a total of affected identities or devices.</p>
          : null}
        <ul className="freshness-partly-areas">
          {areas.map((a) => (
            <li key={a.area} className="freshness-partly-area">
              <span className="freshness-partly-area-name">{a.area}</span>
              {a.checks.length > 0 ? (
                <ul className="freshness-partly-checks">
                  {a.checks.map((c) => (
                    <li key={c.key} className="freshness-partly-check">
                      {checkName(c)}
                      <ul className="freshness-gap-reasons">
                        {gapDescriptions(c).map((reason, i) => <li key={i}>{reason}</li>)}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : (
                // No check could be named from what this server sent. The area stays, with what the
                // assessment recorded, rather than being counted above and missing here.
                <p className="freshness-partly-unnamed">
                  The checks could not be named here. The assessment recorded: {a.unnamed}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </details>
  )
}

/**
 * When the scan ran, absolute and in the reader's own timezone.
 *
 * Not "2 hours ago". A relative time is read once and remembered as if it stays true, and this
 * page can sit open on a wall display for a day. An exact instant cannot go stale that way.
 */
function AsOf({ at, inline = false }: { at: string; inline?: boolean }) {
  const when = new Date(at)
  const text = Number.isNaN(when.getTime())
    ? at
    : when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

  return <span className={inline ? 'freshness-asof-inline' : 'freshness-asof'}>As of {text}</span>
}

function Spinner() {
  return <span className="freshness-spinner" aria-hidden="true" />
}
